<template>
    <div class="table" :class="[$mq]">
        <header class="header">
            <span class="title">{{ title }}</span>
            <div class="actions">
                <div class="timeframe-options">
                    <div class="label">{{ $t('sales.table.timeframe.label') }}</div>
                    <span class="option" :class="{'selected': timeframe == 30}" @click="updateTimeframe(30)">30 {{ $t('sales.table.timeframe.option.minutes') }}</span>
                    <span class="option" :class="{'selected': timeframe == 60}" @click="updateTimeframe(60)">1 {{ $t('sales.table.timeframe.option.hours') }}</span>
                </div>

            </div>
        </header>
        <div class="content">
            <table id="table">
                <thead>
                    <tr>
                        <th v-for="header in headers" :key="header.key" :class="{ 'sticky-column': header.key === 'hour' }">{{ header.label }}</th>
                    </tr>
                </thead>
                <tbody v-if="rows">
                    <tr v-for="row in rows" :key="row.hour">
                        <td class="sticky-column">{{ row.hour }}</td>
                        <td>{{ row.forecast }}</td>
                        <td>{{ row.actual }}</td>
                        <td class="difference" :class="row.difference_class">{{ row.difference }}</td>
                        <td class="difference-percentage" :class="row.difference_class">{{ row.difference_percentage }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>
<script>

import { formatCurrency } from "@/utils/formatters";

export default {
    name: 'tableView',
    props: {
        data: {
            type: Object
        },
        title: { type: String }
    },
    data() {
        return {
            timeframe: localStorage.getItem('sales_timeframe') ? parseInt(localStorage.getItem('sales_timeframe'), 10) : 30,
            headers: [
                {
                    'label': this.$t('sales.table.header.hour'),
                    'key': 'hour'
                },
                {
                    'label': this.$t('sales.table.header.forecast'),
                    'key': 'forecast'
                },
                {
                    'label': this.$t('sales.table.header.actual'),
                    'key': 'actual'
                },
                {
                    'label': this.$t('sales.table.header.difference'),
                    'key': 'difference'
                },
                {
                    'label': this.$t('sales.table.header.difference_percentage'),
                    'key': 'difference_percentage'
                }
            ]
        }
    },
    computed: {
        rows() {
            // Validar que data y forecast existan
            if (!this.data || !this.data.forecast || typeof this.data.forecast !== 'object') {
                return []
            }

            // group key by timeframe
            var timeframe = this.timeframe
            // Agrupar por intervalos de timeframe (30 o 60 minutos)
            // Crear objetos para forecast y actual agrupados por el label de grupo
            const groupedForecast = {};
            const groupedActual = {};

            Object.keys(this.data.forecast).forEach(key => {
                // Redondea el minuto abajo según el timeframe
                const date = moment(key);
                let roundedMinute = 0;
                if (this.timeframe === 60) {
                    roundedMinute = 0;
                } else if (this.timeframe === 30) {
                    roundedMinute = date.minutes() < 30 ? 0 : 30;
                }
                const groupLabel = date.clone().minutes(roundedMinute).seconds(0).format('YYYY-MM-DD HH:mm');

                // Inicializa si no existe el grupo
                if (!groupedForecast[groupLabel]) {
                    groupedForecast[groupLabel] = [];
                }
                if (!groupedActual[groupLabel]) {
                    groupedActual[groupLabel] = [];
                }

                groupedForecast[groupLabel].push(this.data.forecast[key]);
                if (this.data.actual && this.data.actual[key] !== undefined) {
                    groupedActual[groupLabel].push(this.data.actual[key]);
                }
            });

            var rows = Object.keys(groupedForecast).map(key => {
                var forecast = groupedForecast[key].reduce((acc, value) => acc + value, 0)
                var actual = groupedActual[key].reduce((acc, value) => acc + value, 0)
                
                var difference = actual - forecast
                var difference_percentage = forecast > 0 ? (difference / forecast) * 100 : 0
                var is_past = moment(key).isBefore(moment())

                var data = {
                    'hour': moment(key).format('HH:mm'),
                    'forecast': formatCurrency(forecast),
                    'actual': formatCurrency(actual),
                    'difference': formatCurrency(difference),
                    'difference_percentage': formatPercentage(difference_percentage, 0),
                    'difference_class': difference > 0 ? 'positive' : difference < 0 ? 'negative' : 'neutral',
                    'is_past': is_past
                }
                if(!is_past) {
                    data.difference_class = 'empty'
                    data.difference_percentage = '-'
                    data.difference = '-'
                }

                return data
            })
            return rows
        }
    },
    methods: {
        updateTimeframe(timeframe) {
            this.timeframe = timeframe
            localStorage.setItem('sales_timeframe', timeframe)
        }

    },
    created() {
    }
}
</script>
<style lang="scss" scoped>
.table {
    display: flex;
    flex-direction: column;
    width: 100%;

    .header {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        padding: 8px;
        border-bottom: 1px solid $neutro;
        .title {
            font-family: $text-bold;
            font-size: 16px;
        }
        .timeframe-options {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 8px;
            .label {
                color: $color-black;
                @include font-size(s);
            }
            .option {
                cursor: pointer;
                @include font-size(s);
                height: 30px;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: $color-white;
                border-radius: 4px;
                padding: 4px 8px;
                
                &.selected {
                    border: 1px solid $main;
                    background-color: $main-t90;
                }
            }
        }
    }

    .content {
        overflow-x: auto;
    }

    #table {
        border-radius: 4px;
        border: 1px solid $neutro;
        width: 100%;

        thead {
            th {
                @include font-size(s);
                font-family: $text-bold;
                background-color: $default-sec-color;
                padding: 8px;
                word-break: break-word;
            }
        }
        tbody {
            tr {
                &:nth-child(even) {
                    background-color: $neutro-t80;
                }
                &:nth-child(odd) {
                    background-color: $color-white;
                }
                td {
                    padding: 4px 8px;
                    min-width: 100px;
                    &.difference {
                        &.positive {
                            
                            &::before {
                                content: '▲';
                                margin-right: 4px;
                                color: $done;
                                font-size: 10px;
                                padding-right: 4px;
                            }
                        }
                        &.negative {
                            
                            &::before {
                                content: '▼';
                                margin-right: 4px;
                                font-size: 10px;
                                color: $error;
                                padding-right: 4px;
                            }
                        }
                        &.neutral {
                        }
                        &.empty {
                            color: $neutro;
                        }
                    }
                    &.difference-percentage {
                        &.positive {
                            color: $done;
                        }
                        &.negative {
                            color: $error;
                        }
                        &.neutral {
                        }
                        &.empty {
                            color: $neutro;
                        }
                    }
                }
            }
        }

        .sticky-column {
            position: sticky;
            left: 0;
            z-index: 10;
            background-color: inherit;
            border-right: 1px solid $neutro-t40;
        }

        thead .sticky-column {
            background-color: $default-sec-color;
        }

        tbody tr:nth-child(even) .sticky-column {
            background-color: $neutro-t80;
        }

        tbody tr:nth-child(odd) .sticky-column {
            background-color: $color-white;
        }
    }

}


@media (orientation: portrait) {

}
</style>

<style lang="scss" scoped>
    .table.portrait {
        #table {
            thead {
                th {
                    @include font-size(xs);
                    line-height: 1
                }
            }
            tbody {
                td {
                    @include font-size(xs);
                }
            }
        }
    
    }
</style>